import { LoadingGate } from "@/components/LoadingGate/LoadingGate";
import { useAuthDetails } from "@/context/auth-context";
import { useCurrentUserGroupRole } from "@/hooks/use-current-user-group-role";
import { useDebugSettings } from "@/hooks/use-debug-settings";
import { type GroupRole, type SystemRole } from "@/lib/consts";
import { cn } from "@/lib/utils";
import { Outlet, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

type RoleGuardProps =
  | { systemRoles: SystemRole[]; groupRoles?: GroupRole[] }
  | { systemRoles?: SystemRole[]; groupRoles: GroupRole[] };

/**
 * If user is not authorized to access the page, navigate back.
 */
export default function RoleGuard({ systemRoles, groupRoles }: RoleGuardProps) {
  const authDetails = useAuthDetails();
  const groupRole = useCurrentUserGroupRole();
  const { overlay } = useDebugSettings();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const redirected = useRef(false);

  const isSuper = authDetails?.role === "super";
  const systemAllowed = systemRoles
    ? systemRoles.includes(authDetails?.role as SystemRole)
    : false;
  const groupAllowed = groupRoles
    ? groupRoles.includes(groupRole as GroupRole)
    : false;
  const allowed =
    authDetails !== undefined && (isSuper || systemAllowed || groupAllowed);

  const allRoles = [...(systemRoles ?? []), ...(groupRoles ?? [])];

  useEffect(() => {
    if (authDetails === undefined) return;
    if (!allowed && !redirected.current) {
      if (window.history.length > 1) {
        window.history.back();
      } else {
        navigate({ to: "/u/workspaces", replace: true });
      }
      redirected.current = true;
    }

    setIsLoading(false);
  }, [authDetails, groupRole]);

  return (
    <>
      <LoadingGate loading={isLoading}>
        <Outlet />
      </LoadingGate>
      {overlay &&
        createPortal(
          <div
            className={cn(
              "fixed z-1001",
              "top-0 left-0 h-full w-full border-[4px]",
              "pointer-events-none",
              {
                "border-red-500": !allowed,
                "border-green-500": allowed,
              },
            )}
          >
            <div
              className={cn(
                "absolute",
                "-top-[4px] -left-[4px] z-1002 px-1 py-0.5",
                "text-center text-[10px] text-white",
                {
                  "bg-red-500": !allowed,
                  "bg-green-500": allowed,
                },
              )}
            >
              RoleGuard: {allRoles.join(", ")}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
